import { template as template_fd9b2463e5f141cfb5666b64daa1f11a } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { element } from 'ember-element-helper';
import classNames from 'volta/helpers/class-names';
import type { ContentValue } from '@glint/template';
type TElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'legend' | 'time';
export type TVariant = 'heading-xs' | 'heading-sm' | 'heading-md' | 'heading-lg' | 'heading-xl' | 'heading-2xl' | 'heading-3xl' | 'heading-4xl' | 'body-sm' | 'body-md' | 'body-lg' | 'code';
type TAlignment = 'start' | 'center' | 'end' | 'justify';
type TFontWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type TColor = 'success' | 'critical' | 'warning' | 'subdued' | 'hud';
const VariantFontWeightMapping: {
    [V in TVariant]: TFontWeight;
} = {
    'heading-xs': 'semibold',
    'heading-sm': 'semibold',
    'heading-md': 'semibold',
    'heading-lg': 'semibold',
    'heading-xl': 'semibold',
    'heading-2xl': 'semibold',
    'heading-3xl': 'semibold',
    'heading-4xl': 'bold',
    'body-sm': 'regular',
    'body-md': 'regular',
    'body-lg': 'regular',
    code: 'regular'
};
const VariantElementMapping: {
    [V in TVariant]: TElement;
} = {
    'heading-xs': 'h6',
    'heading-sm': 'h6',
    'heading-md': 'h6',
    'heading-lg': 'h5',
    'heading-xl': 'h4',
    'heading-2xl': 'h3',
    'heading-3xl': 'h2',
    'heading-4xl': 'h1',
    'body-sm': 'span',
    'body-md': 'span',
    'body-lg': 'span',
    code: 'span'
};
const CLASS_NAME = 'bv-text';
const cssMod = (mod?: string)=>(mod ? `${CLASS_NAME}--${mod}` : undefined);
export interface BvTextSignature {
    Args: {
        /** Adjust horizontal alignment of text */ alignment?: TAlignment;
        /** The element type */ as?: TElement;
        /** Prevent text from overflowing */ breakWord?: boolean;
        /** Text to display */ text?: ContentValue;
        /** Adjust color of text */ color?: TColor;
        /** Adjust weight of text */ fontWeight?: TFontWeight;
        /** HTML id attribute */ id?: string;
        /** Truncate text overflow with ellipsis */ truncate?: boolean;
        /** Typographic style of text */ variant?: TVariant;
        /** Visually hide the text */ visuallyHidden?: boolean;
        class?: string;
    };
    Blocks: {
        default: [];
    };
    Element: HTMLElement;
}
export default class BvTextComponent extends Component<BvTextSignature> {
    get tagName() {
        const { as, variant } = this.args;
        return (as ?? (variant && VariantElementMapping[variant] ? VariantElementMapping[variant!] : 'span'));
    }
    get classNames() {
        const { variant, fontWeight, alignment, truncate, breakWord, color, visuallyHidden } = this.args;
        const classes = [
            // Root
            CLASS_NAME,
            // Variant
            cssMod(variant),
            // Font Weight
            fontWeight ? cssMod(fontWeight) : cssMod(variant ? VariantFontWeightMapping[variant] : undefined),
            // Block
            (alignment ?? truncate) && cssMod('block'),
            // Alignment
            alignment && cssMod(alignment),
            // Break Word
            breakWord && cssMod('break'),
            // Color
            color && cssMod(color),
            // Truncate
            truncate && cssMod('truncate'),
            // Visually Hidden
            visuallyHidden && cssMod('visually-hidden')
        ];
        return classes.filter(Boolean).join(' ');
    }
    static{
        template_fd9b2463e5f141cfb5666b64daa1f11a(`
    {{#let (element this.tagName) as |Text|}}
      <Text class={{classNames this.classNames @class}} ...attributes>
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{@text}}
        {{/if}}
      </Text>
    {{/let}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
declare module '@glint/environment-ember-loose/registry' {
    export default interface Registry {
        BvText: typeof BvTextComponent;
        'bv-text': typeof BvTextComponent;
    }
}
